/**
 * The portal's named date styles, all in the reader's own locale and time
 * zone. Used by the places pages; `all-brands-page.tsx` and `members-page.tsx`
 * still format their own dates (to move here later).
 */

/** When something happened or starts: "Tue, 29 Sep, 3:24 PM"; the year only
 *  when it is not this year. `zoned` names the time zone ("… 7:00 PM PDT"),
 *  for times still to come. In the reader's own time zone and locale unless
 *  `timeZone` (an IANA name) or `locale` is given. */
export function whenLabel(
  at: number,
  {
    now = Date.now(),
    timeZone,
    locale,
    zoned = false,
  }: { now?: number; timeZone?: string; locale?: string; zoned?: boolean } = {},
): string {
  const year = (t: number) =>
    timeZone ? new Date(t).toLocaleString("en-US", { year: "numeric", timeZone }) : String(new Date(t).getFullYear());
  return new Date(at).toLocaleString(locale, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: year(at) === year(now) ? undefined : "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: zoned ? "short" : undefined,
    timeZone,
  });
}

/** When something runs, start to end, its time zone named: "Tue, Oct 7, 7:00 –
 *  8:00 PM PDT" (the day once when both ends fall on it). In the reader's own
 *  time zone and locale unless `timeZone` or `locale` is given. */
export function windowLabel(
  start: number,
  end: number,
  { timeZone, locale }: { timeZone?: string; locale?: string } = {},
): string {
  return new Intl.DateTimeFormat(locale, {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
    timeZone,
  }).formatRange(start, end);
}

/** A moment as a `datetime-local` input holds it, in the reader's own time
 *  zone, to the minute: "2026-10-07T19:00". */
export function localInputValue(at: number): string {
  const d = new Date(at);
  const two = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}T${two(d.getHours())}:${two(d.getMinutes())}`;
}

/** A `datetime-local` input's value as a moment, read in the reader's own
 *  time zone (a date-time with no offset is local time); NaN when blank or
 *  not a date. */
export function fromLocalInput(value: string): number {
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value) ? new Date(value).getTime() : NaN;
}

/** The time of day a one-off read was made ("3:24 PM"), for "Read at …". */
export function readAtLabel(readAt: number): string {
  return new Date(readAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

/** A time of day from its hour (0–23) and minute, in no time zone: "7:00 PM". */
export function clockLabel(hour: number, minute: number, locale?: string): string {
  return new Date(Date.UTC(2000, 0, 1, hour, minute)).toLocaleTimeString(locale, {
    hour: "numeric",
    minute: "2-digit",
    timeZone: "UTC",
  });
}
